import { z } from 'zod';

// Common validation schemas
export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Please enter a valid email address');

// A new password needs only to be present here: the server judges it
// (auth-service credentials.ValidatePassword) and its refusal sentence is
// shown (agion/Agion#3140, B4). The screens show the rule as a hint from
// src/generated/passwordRule.ts.
const newPasswordSchema = z.string().min(1, 'Password is required');

export const nameSchema = z
  .string()
  .min(1, 'Name is required')
  .min(2, 'Name must be at least 2 characters')
  .max(50, 'Name must be less than 50 characters')
  .regex(/^[a-zA-Z\s-']+$/, 'Name can only contain letters, spaces, hyphens, and apostrophes');

// Form validation schemas
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required'),
  remember: z.boolean().optional(),
});

export const registerSchema = z.object({
  email: emailSchema,
  password: newPasswordSchema,
  confirmPassword: z.string().min(1, 'Please confirm your password'),
  firstName: nameSchema,
  lastName: nameSchema,
  terms: z.boolean().refine(val => val === true, 'You must accept the terms and conditions'),
}).refine(data => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export const resetPasswordSchema = z.object({
  token: z.string().min(1, 'Token is required'),
  password: newPasswordSchema,
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine(data => data.password === data.confirmPassword, {
  message: 'Passwords do not match',
  path: ['confirmPassword'],
});

export const mfaSchema = z.object({
  code: z.string().min(6, 'Code must be 6 digits').max(6, 'Code must be 6 digits'),
  method: z.enum(['totp', 'webauthn', 'sms', 'email', 'backup']),
  challenge: z.string().min(1, 'Challenge is required'),
});

export const consentSchema = z.object({
  challenge: z.string().min(1, 'Challenge is required'),
  accept: z.boolean(),
  remember: z.boolean().optional(),
  grantScope: z.array(z.string()).optional(),
});

// Blocked consumer/disposable email domains — mirrors auth-service defaultBlockedDomains
const blockedEmailDomains = new Set([
  'gmail.com', 'googlemail.com',
  'yahoo.com', 'yahoo.co.uk',
  'hotmail.com', 'outlook.com', 'live.com',
  'aol.com',
  'icloud.com', 'me.com',
  'mail.com',
  'protonmail.com', 'proton.me',
  'zoho.com',
  'yandex.com',
  'mailinator.com', 'guerrillamail.com', 'tempmail.com',
  'throwaway.email', '10minutemail.com', 'trashmail.com',
])

/**
 * Returns true if the email uses a blocked consumer/disposable domain.
 * Used for waitlist signup to ensure only work emails are accepted.
 */
export function isBlockedEmailDomain(email: string): boolean {
  const parts = email.toLowerCase().trim().split('@')
  const domain = parts[1]
  if (parts.length !== 2 || domain === undefined) return true
  return blockedEmailDomains.has(domain)
}

/**
 * Email shape from the design kit: something, @, a domain with a dot, no spaces.
 * Loose on purpose; a form uses it to catch a name typed into the email field,
 * and the server decides whether the address is real.
 */
export function hasEmailShape(email: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)
}

// Utility functions
export function validateEmail(email: string): boolean {
  return emailSchema.safeParse(email).success;
}

export function getPasswordStrength(password: string): {
  score: number;
  feedback: string[];
} {
  const feedback: string[] = [];
  let score = 0;

  if (password.length >= 8) score += 1;
  else feedback.push('Use at least 8 characters');

  if (/[A-Z]/.test(password)) score += 1;
  else feedback.push('Include uppercase letters');

  if (/[a-z]/.test(password)) score += 1;
  else feedback.push('Include lowercase letters');

  if (/\d/.test(password)) score += 1;
  else feedback.push('Include numbers');

  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score += 1;
  else feedback.push('Include special characters');

  if (password.length >= 12) score += 1;
  if (!/(.)\1{2,}/.test(password)) score += 1; // No repeating characters

  return { score, feedback };
}

export function sanitizeInput(input: string): string {
  return input.trim().replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
}

export function isValidURL(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export const validateUrl = isValidURL;